/**
 * src/pages/WhereIsEverythingGoing/sections/HumanFuturesSection.tsx
 * "Possible futures for humanity": ExpandList of technological and civilizational futures.
 * Labelled research or speculative:
 * - climate adaptation and clean energy (research)
 * - spaceflight & settling other worlds (research/speculative)
 * - artificial intelligence (research; wide disagreement among experts)
 * - biotechnology and longevity (research)
 * - becoming a Kardashev Type I civilization (link to /KardashevScalePage)
 * - existential risks and long-term thinking (Ord 2020 The Precipice; philosophy)
 * Clear statement of wide uncertainty and absence of deterministic timelines.
 */

import React from "react";
import { ExpandList } from "@/components/originBeyond/ExpandList";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Link } from "@/components/common/Link";
import { Footnote } from "@/components/originBeyond/Footnote";
import type { ExpandItem } from "@/lib/originBeyond/types";
import { ShieldCheck, ArrowRight } from "lucide-react";
export function HumanFuturesSection() {
  const items: ExpandItem[] = [{
    id: "fut-climate",
    title: "Climate Adaptation, Deep Decarbonization & Earth Stewardship",
    level: "research",
    summary: "Energy transition, planetary boundaries, and carbon capture infrastructure.",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            The physical requirements for halting atmospheric warming are mathematically
            explicit: global anthropogenic carbon dioxide emissions must reach net zero.
            Ongoing research models pathways spanning rapid solar, wind, and advanced
            nuclear deployment alongside industrial direct air capture and biosphere
            restoration.
          </p>
          <p>
            While geophysical constraints dictate atmospheric radiative forcing, the rate
            of infrastructure replacement remains a sociological and political choice rather
            than a physical impossibility.
          </p>
        </div>,
    refs: [9]
  }, {
    id: "fut-spaceflight",
    title: "Interplanetary Spaceflight & Off-World Biospheres",
    level: "speculative",
    summary: "Orbital industry, lunar bases, and closed-loop ecological life support.",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            Humanity's expansion beyond low Earth orbit transitions from state-funded
            exploration to potential multi-planetary permanent settlement. Initial
            footholds on the Moon and Mars test artificial closed-loop biosystems, radiation
            shielding, and in-situ resource utilization.
          </p>
          <p>
            Whether self-sustaining industrial civilizations can thrive without continuous
            supply chains from Earth's deep biological web remains an open empirical
            question.
          </p>
        </div>
  }, {
    id: "fut-ai",
    title: "Artificial General Intelligence & Autonomous Reasoning",
    level: "research",
    summary: "Dispute among computer scientists regarding timelines, alignment, and cognitive architecture.",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            Frontier machine learning systems demonstrate rapid advances in mathematical
            problem solving, scientific reasoning, and synthetic biology design. However,
            there is wide disagreement among theoretical computer scientists and cognitive
            researchers regarding whether scaling transformer architectures leads to true
            general intelligence or whether fundamental conceptual breakthroughs in causal
            reasoning are mandatory.
          </p>
          <p>
            The alignment of superhuman optimization processes with human safety and
            flourishing remains one of the century's most active and critical research
            domains.
          </p>
        </div>,
    refs: [9]
  }, {
    id: "fut-biotech",
    title: "Biotechnology, Genetic Medicine & Radical Longevity",
    level: "research",
    summary: "Cellular rejuvenation, epigenetic reprogramming, and synthetic genomics.",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            Investigating the biological hallmarks of aging—including telomere attrition,
            cellular senescence, and epigenetic degradation—has yielded experimental
            therapies that extend healthspan in model organisms. Precision CRISPR gene
            editing and mRNA therapeutics already treat previously intractable hereditary
            diseases.
          </p>
          <p>
            Whether human lifespan can be extended significantly beyond the natural
            Gompertz mortality limits remains uncertain and bounded by systemic cellular
            complexity.
          </p>
        </div>
  }, {
    id: "fut-kardashev",
    title: "Transition to a Kardashev Type I Planetary Civilization",
    level: "speculative",
    summary: "Harnessing the full solar power incident on Earth (~10¹⁷ Watts).",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            In 1964, Nikolai Kardashev proposed classifying technological civilizations by
            their power consumption. Carl Sagan later mathematically calibrated Type I to a
            continuous energetic consumption of 10¹⁶ to 10¹⁷ Watts—equivalent to the total
            stellar irradiance absorbed by Earth.
          </p>
          <p>
            Current human civilization operates at roughly Type 0.73. Reaching Type I requires
            mastering planetary-scale clean energy without destabilizing Earth's thermodynamic
            heat balance.
          </p>
          <div className="pt-2">
            <Link to="/KardashevScalePage" className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-primary hover:underline">
              <span>Explore the interactive Kardashev Scale portal</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>,
    refs: [8]
  }, {
    id: "fut-existential",
    title: "Existential Risk Mitigation & Longtermist Philosophy",
    level: "philosophy",
    summary: "Navigating anthropogenic bottlenecks to protect humanity's vast potential.",
    body: <div className="space-y-3 font-sans text-sm sm:text-base leading-relaxed">
          <p>
            In <em>The Precipice</em>, philosopher Toby Ord argues that humanity has entered
            a uniquely hazardous epoch: our technological capability to alter the world has
            outpaced our collective wisdom. Anthropogenic hazards—including unaligned AI,
            engineered pandemics, and nuclear conflict—present non-trivial existential risks
            over the coming century.
          </p>
          <p>
            Longtermism posits that because the overwhelming majority of potential human
            lives exist in the deep future, ensuring our near-term survival and moral maturity
            is our paramount ethical responsibility.
          </p>
        </div>,
    refs: [9]
  }];
  return <section id="human-futures" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="human-futures-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Anthropological Horizons & Uncertainty" title="Possible Futures for Humanity" description="Technology expands our agency across energy, biology, and computation. However, no historical timeline is predetermined: outcomes remain subject to scientific discovery, ethical choices, and severe epistemic uncertainty." />

        {/* Wide Uncertainty Disclaimer Banner */}
        <div className="ql-glass p-5 sm:p-6 mb-8 rounded-xl border border-primary/30 flex items-start gap-3.5 text-xs sm:text-sm text-muted-foreground font-sans min-w-0">
          <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="leading-relaxed break-words">
            <strong className="text-foreground">Uncertainty Declaration:</strong> None of
            the trajectories described below are inevitable prophecies. They represent
            active engineering research programs and reasoned philosophical scenarios.
            Societal collapse, stagnation, or unimaginable creative flourishing remain
            equally within the cone of biological possibility.
          </div>
        </div>

        {/* Expandable Accordion List */}
        <div className="w-full min-w-0">
          <ExpandList items={items} allowMultiple defaultOpenId="fut-climate" />
        </div>
      </div>
    </section>;
}